import {
  Box,
  Text,
  Button,
  Stack,
  Icon,
  HStack,
  Spacer,
  ButtonGroup,
  Heading,
  TabPanel,
  TabList,
  Tab,
  Tabs,
  TabPanels,
  Badge,
  Link,
  Spinner,
  useDisclosure,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@chakra-ui/react';
import {
  BillingAccountSubscriptionResponse,
  TimeAndDate,
  ManualBillingAccountListingResponse,
} from '@zap-onboard/api-client';
import { FaMoneyBill } from 'react-icons/fa';
import { DeleteModal } from '../components';
import { ErrorDisplay } from './ErrorDisplay';
import { useBillingAccountSubscriptions } from './hooks/useBillingAccountSubscriptions';
import { useBillingMutations } from './hooks/useBillingMutations';

export const ManualAccountListing = (args: {
  billingAccount: ManualBillingAccountListingResponse;
}) => {
  const a = args.billingAccount;

  return (
    <Box layerStyle="base">
      <HStack p={6}>
        <HStack>
          <Heading variant="subSection">{a.name ?? a.billingAccountId}</Heading>
          <Badge>Active</Badge>
        </HStack>
        <Spacer />

        <Icon color="brand.dark-gray" w={8} h={8} as={FaMoneyBill} />
      </HStack>

      <Tabs variant="line" size="sm" isLazy>
        <TabList flexDir={{ sm: 'row', base: 'column' }}>
          <Tab>Businesses</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={6}>
            <BusinessPanel billingAccount={a} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const BusinessPanel = ({
  billingAccount,
}: {
  billingAccount: ManualBillingAccountListingResponse;
}) => {
  const subscriptions = useBillingAccountSubscriptions({
    billingAccountId: billingAccount.billingAccountId,
  });

  if (!subscriptions.isFetched) {
    return <Spinner />;
  }
  const errors = (
    <ErrorDisplay title="Could not load your subscription" {...subscriptions} />
  );
  if (subscriptions.data == null) {
    return errors;
  }

  return (
    <Stack spacing={6}>
      {errors}
      <Stack spacing={3}>
        {subscriptions.data?.map((sub) => (
          <BusinessRow
            key={sub.businessId}
            billingAccountId={billingAccount.billingAccountId}
            subscription={sub}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const LinkedBusinessPanel = ({
  billingAccount,
}: {
  billingAccount: ManualBillingAccountListingResponse;
}) => {
  const subscriptions = useBillingAccountSubscriptions({
    billingAccountId: billingAccount.billingAccountId,
  });

  if (!subscriptions.isFetched) {
    return <Spinner />;
  }
  const errors = (
    <ErrorDisplay title="Could not load your subscription" {...subscriptions} />
  );
  if (subscriptions.data == null) {
    return errors;
  }

  return (
    <Stack spacing={6}>
      <Stack spacing={3}>
        {subscriptions.data.length > 0 ? (
          subscriptions.data?.map((sub) => (
            <BusinessRow
              key={sub.businessId}
              billingAccountId={billingAccount.billingAccountId}
              subscription={sub}
            />
          ))
        ) : (
          <Text>No linked businesses</Text>
        )}
      </Stack>
    </Stack>
  );
};

const BusinessRow = ({
  billingAccountId,
  subscription: { businessId, businessName },
}: {
  billingAccountId: string;
  subscription: BillingAccountSubscriptionResponse;
}) => {
  const disclosure = useDisclosure();
  const { unsubscribe } = useBillingMutations();
  return (
    <HStack p={3} layerStyle="outline" key={businessId}>
      <Text fontWeight="bold">{businessName}</Text>
      <Spacer />
      <Button onClick={disclosure.onOpen} variant="outline">
        Remove from billing account
      </Button>
      <DeleteModal
        {...disclosure}
        modalBodyText="This will remove the business from your billing account."
        modalHeaderText={`Remove ${businessName} from your Subscription`}
        confirmText="Remove"
        callBack={() =>
          unsubscribe({
            businessId,
            billingAccountId,
          })
        }
      />
    </HStack>
  );
};
